import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo, useEffect, useCallback } from "react";
import { LinkButton, LoadingBar, ExportButton, exportToExcelWithXLSX, ExportFieldCustomizer, } from "@perfectserve/library";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog } from "@fortawesome/pro-solid-svg-icons";
import { INTERACTION_EXPORT_FIELDS, EXPORT_FIELDS_CONFIG_VERSION, } from "../types/interactions";
import { formatDuration, formatPhoneNumber, formatInteractionForDisplay } from "../utils/formatters";
import { formatInteractionDateTime } from "../utils/dateUtils";
import HelpDialog from "./HelpDialog";
import KPIDisplayBanner from "./KPIDisplayBanner";
import InteractionList from "../views/InteractionList";
import { useInteractionMetrics } from "../hooks/useInteractionMetrics";
import { useKPIResult, useKPIFiltering } from "../hooks/useKPIFiltering";
import { parseDateToLocal } from "../utils/chartUtils";
import { generateVolumeChartData } from "../utils/chartUtils";
import { generateMetricCards } from "../utils/metricCardsUtils";
import { getDistinctScheduleNames, getDistinctObjectNames } from "../utils/scheduleNameUtils";
import { extractScheduleNames } from "../utils/callFlowUtils";
import DynamicBarChart from "./InteractionsSummaryCard/DynamicBarChart";
import DistributionGraphs from "./InteractionsSummaryCard/DistributionGraphs";
import PracticesFilter from "./InteractionsSummaryCard/PracticesFilter";
import ScheduleFilter from "./InteractionsSummaryCard/ScheduleFilter";
import ActionLinks from "./InteractionsSummaryCard/ActionLinks";
const InteractionsSummaryCard = ({ onCallOutcomesClick, interactions, title = "Interactions", subtitle, isAggregateCard = false, isLoading, showViewSettingsButton = false, onViewSettingsClick, viewSettings, dateRange, entityLoadingStates: _entityLoadingStates, entityId: _entityId, selectedKPI, savedKPIs: _savedKPIs, onKPIChange: _onKPIChange, onSaveKPI: _onSaveKPI, onOpenKPIModal, onTotalInteractionsClick, selectedAccountNames, selectedUserNames, onExecutiveSummaryClick, onResponseMetricsClick, onPresentationClick, onPdfExportClick, }) => {
    const [selectedObjectName, setSelectedObjectName] = useState("All");
    const [selectedScheduleName, setSelectedScheduleName] = useState("All");
    const [showInlineInteractionList, setShowInlineInteractionList] = useState(false);
    const [inlineInteractionData, setInlineInteractionData] = useState({ interactions: [], title: "" });
    const [cardContentVisible, setCardContentVisible] = useState(true);
    const [cardInitiallyVisible, setCardInitiallyVisible] = useState(false);
    const [cardDataVisible, setCardDataVisible] = useState(false);
    const [showFieldCustomizer, setShowFieldCustomizer] = useState(false);
    const isDataLoaded = interactions !== null;
    const filteredInteractions = useKPIFiltering(interactions || [], selectedKPI);
    const aggregateCardLoading = isLoading || !isDataLoaded || (isDataLoaded && interactions.length === 0);
    const actualIsLoading = isAggregateCard ? aggregateCardLoading : (isLoading !== null && isLoading !== void 0 ? isLoading : false);
    const hasData = isDataLoaded && interactions && interactions.length > 0;
    const shouldShowLoading = !isDataLoaded;
    const hasNoAPIData = (!interactions || interactions.length === 0) && isDataLoaded;
    const [showHelpDialog, setShowHelpDialog] = useState(false);
    const [kpiBannerVisible, setKpiBannerVisible] = useState(false);
    const distinctObjectNames = useMemo(() => getDistinctObjectNames(interactions || []), [interactions]);
    const distinctScheduleNames = useMemo(() => getDistinctScheduleNames(interactions || []), [interactions]);
    const locallyFilteredInteractions = useMemo(() => {
        if (!filteredInteractions)
            return [];
        let practiceFiltered = filteredInteractions;
        if (selectedObjectName !== "All") {
            practiceFiltered = filteredInteractions.filter((i) => i.ToFilterObjectName === selectedObjectName);
        }
        let scheduleFiltered = practiceFiltered;
        if (selectedScheduleName !== "All") {
            scheduleFiltered = practiceFiltered.filter((interaction) => {
                if (!interaction.CallFlow)
                    return false;
                const schedules = extractScheduleNames(interaction.CallFlow);
                return schedules.includes(selectedScheduleName);
            });
        }
        if (!viewSettings.excludeHangupsAndDisconnects) {
            return scheduleFiltered;
        }
        const excludedActions = ["HangUp", "Hangup", "Disconnect", "Disconnected"];
        return scheduleFiltered.filter((interaction) => !excludedActions.includes(interaction.InteractionFinalAction || ""));
    }, [filteredInteractions, selectedObjectName, selectedScheduleName, viewSettings.excludeHangupsAndDisconnects]);
    const interactionsExcludingHangups = useMemo(() => {
        if (!viewSettings.excludeHangupsAndDisconnects) {
            return interactions || [];
        }
        const excludedActions = ["HangUp", "Hangup", "Disconnect", "Disconnected"];
        return (interactions || []).filter((interaction) => !excludedActions.includes(interaction.InteractionFinalAction || ""));
    }, [interactions, viewSettings.excludeHangupsAndDisconnects]);
    const summaryMetrics = useInteractionMetrics(locallyFilteredInteractions, interactions || []);
    const volumeChartData = useMemo(() => generateVolumeChartData(locallyFilteredInteractions, viewSettings, dateRange), [locallyFilteredInteractions, viewSettings, dateRange]);
    const priorityData = useMemo(() => {
        const total = locallyFilteredInteractions.length;
        const urgent = summaryMetrics.urgent;
        const urgentPct = total > 0 ? Math.round((urgent / total) * 100) : 0;
        return { urgentPct, routinePct: 100 - urgentPct };
    }, [locallyFilteredInteractions.length, summaryMetrics.urgent]);
    const originatorData = useMemo(() => {
        const total = locallyFilteredInteractions.length;
        const patient = locallyFilteredInteractions.filter((i) => i.ToFilterOriginator === "Patient").length;
        const patientPct = total > 0 ? Math.round((patient / total) * 100) : 0;
        return { patientPct, providerPct: 100 - patientPct };
    }, [locallyFilteredInteractions]);
    const kpiResult = useKPIResult(interactions || [], selectedKPI);
    useEffect(() => {
        const timer = setTimeout(() => {
            setCardInitiallyVisible(true);
        }, 50);
        return () => clearTimeout(timer);
    }, []);
    useEffect(() => {
        if (isAggregateCard) {
            const timer = setTimeout(() => {
                setCardDataVisible(true);
            }, 100);
            return () => clearTimeout(timer);
        }
        else if (isDataLoaded) {
            const timer = setTimeout(() => {
                setCardDataVisible(true);
            }, 150);
            return () => clearTimeout(timer);
        }
        else {
            setCardDataVisible(false);
            return undefined;
        }
    }, [isDataLoaded, isAggregateCard]);
    useEffect(() => {
        if (isDataLoaded && kpiResult) {
            const timer = setTimeout(() => {
                setKpiBannerVisible(true);
            }, 250);
            return () => clearTimeout(timer);
        }
        else {
            setKpiBannerVisible(false);
            return undefined;
        }
    }, [isDataLoaded, kpiResult]);
    const handleOpenInteractionList = useCallback((filteredInteractions, modalTitle) => {
        setCardContentVisible(false);
        setTimeout(() => {
            setInlineInteractionData({
                interactions: filteredInteractions,
                title: modalTitle,
            });
            setShowInlineInteractionList(true);
            setTimeout(() => {
                setCardContentVisible(true);
            }, 50);
        }, 300);
    }, []);
    const handleCloseInlineInteractionList = useCallback(() => {
        setCardContentVisible(false);
        setTimeout(() => {
            setShowInlineInteractionList(false);
            setInlineInteractionData({ interactions: [], title: "" });
            setTimeout(() => {
                setCardContentVisible(true);
            }, 50);
        }, 300);
    }, []);
    const handleTotalInteractionsClick = useCallback(() => {
        handleOpenInteractionList(locallyFilteredInteractions, `${title} - All Interactions`);
        if (onTotalInteractionsClick) {
            onTotalInteractionsClick();
        }
    }, [locallyFilteredInteractions, title, handleOpenInteractionList, onTotalInteractionsClick]);
    const handleTransfersClick = useCallback(() => {
        const transferInteractions = locallyFilteredInteractions.filter((i) => {
            const action = i.InteractionFinalAction || "";
            const normalizedAction = action.replace(/\s+/g, "");
            return (normalizedAction === "BlindTransfer" ||
                normalizedAction === "ControlledTransfer" ||
                normalizedAction === "HandoffTransfer");
        });
        handleOpenInteractionList(transferInteractions, `${title} - Transfers`);
    }, [locallyFilteredInteractions, title, handleOpenInteractionList]);
    const handleMetricClick = useCallback((metricLabel) => {
        if (metricLabel === "Total Interactions") {
            handleTotalInteractionsClick();
        }
        else if (metricLabel === "Transfers") {
            handleTransfersClick();
        }
    }, [handleTotalInteractionsClick, handleTransfersClick]);
    const handleChartBarDoubleClick = useCallback((data, _index) => {
        if (!data || !data.period)
            return;
        const period = data.period;
        const chartDataRange = viewSettings.chartDataRange;
        const periodInteractions = locallyFilteredInteractions.filter((interaction) => {
            const dateString = interaction.InteractionStartDate || interaction.InteractionStartDateUtc;
            if (!dateString)
                return false;
            try {
                const localDate = parseDateToLocal(dateString);
                if (isNaN(localDate.getTime()))
                    return false;
                switch (chartDataRange) {
                    case "Daily": {
                        const interactionPeriod = `${localDate.getMonth() + 1}/${localDate.getDate()}`;
                        return interactionPeriod === period;
                    }
                    case "Weekly": {
                        const weekStart = new Date(localDate);
                        weekStart.setDate(localDate.getDate() - localDate.getDay());
                        const interactionPeriod = `${weekStart.getMonth() + 1}/${weekStart.getDate()}`;
                        return interactionPeriod === period;
                    }
                    case "Monthly": {
                        const interactionPeriod = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, "0")}`;
                        return interactionPeriod === period;
                    }
                    default:
                        return false;
                }
            }
            catch (error) {
                return false;
            }
        });
        const modalTitle = `${title} - ${period} (${periodInteractions.length} interactions)`;
        handleOpenInteractionList(periodInteractions, modalTitle);
    }, [locallyFilteredInteractions, title, handleOpenInteractionList, viewSettings.chartDataRange]);
    const handleKPIPerformanceClick = useCallback((threshold, kpiInteractions) => {
        var _a;
        const kpiName = ((_a = kpiResult === null || kpiResult === void 0 ? void 0 : kpiResult.kpiConfig) === null || _a === void 0 ? void 0 : _a.name) || "KPI";
        handleOpenInteractionList(kpiInteractions, `${title} - ${kpiName} ${threshold}`);
    }, [kpiResult, title, handleOpenInteractionList]);
    const metricCards = generateMetricCards(viewSettings, summaryMetrics);
    const availableExportFields = useMemo(() => {
        return INTERACTION_EXPORT_FIELDS.map((field) => field.key);
    }, []);
    const handleExcelExport = useCallback(async () => {
        try {
            const exportData = inlineInteractionData.interactions.map((interaction) => {
                const formatted = formatInteractionForDisplay(interaction, formatInteractionDateTime);
                return {
                    ...formatted,
                    FromCallbackNumber: formatPhoneNumber(interaction.FromCallbackNumber),
                    FormattedStartDate: formatInteractionDateTime(interaction.InteractionStartDateUtc),
                    FormattedEndDate: interaction.InteractionEndDateUtc
                        ? formatInteractionDateTime(interaction.InteractionEndDateUtc)
                        : "—",
                    FormattedNotified: formatDuration(interaction.NotificationInSeconds),
                    FormattedReadTime: formatDuration(interaction.ReadInSeconds),
                    FormattedCallback: formatDuration(interaction.CallbackInSeconds),
                };
            });
            const savedConfig = localStorage.getItem("interaction-summary-export-fields");
            let fieldConfig;
            if (savedConfig) {
                try {
                    fieldConfig = JSON.parse(savedConfig);
                }
                catch (_a) {
                    fieldConfig = undefined;
                }
            }
            await exportToExcelWithXLSX({
                filename: inlineInteractionData.title.replace(/[^a-zA-Z0-9]/g, "_"),
                data: exportData,
                sheetName: "Interactions",
                fieldConfig,
            });
        }
        catch (error) {
            console.error("Excel export failed:", error);
            alert("Excel export failed. Please try again.");
        }
    }, [inlineInteractionData]);
    const handleFieldCustomizationSave = useCallback((_fields) => {
    }, []);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: `
        interactions-summary-card
        ${isAggregateCard ? "interactions-summary-card--aggregate" : "interactions-summary-card--individual"}
        ${shouldShowLoading ? "interactions-summary-card--loading" : ""}
        ${showInlineInteractionList ? "interactions-summary-card--interaction-list" : ""}
      `.trim(), style: {
                    backgroundColor: "white",
                    position: "relative",
                    opacity: cardInitiallyVisible ? 1 : 0,
                    transition: "opacity 0.4s ease-in-out",
                }, children: [shouldShowLoading && (_jsxs("div", { style: {
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "white",
                            display: "flex",
                            flexDirection: "column",
                            zIndex: 5,
                            opacity: shouldShowLoading ? 1 : 0,
                            transition: "opacity 0.3s ease-in-out",
                        }, children: [_jsxs("div", { className: "interactions-summary-card__header", style: {
                                    paddingBottom: "10px",
                                }, children: [_jsx("div", { style: {
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                            marginLeft: "10px",
                                        }, children: _jsx("div", { children: subtitle && (_jsx("p", { style: {
                                                    margin: "4px 0 0 0",
                                                    fontSize: "14px",
                                                    color: "var(--text-secondary, #666)",
                                                    fontWeight: "normal",
                                                }, children: subtitle })) }) }), _jsx("div", { className: "d-flex align-items-center gap-2" })] }), _jsx("div", { className: "interactions-summary-card__content", style: {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    color: "var(--text-muted, #999)",
                                }, children: _jsx("div", { className: "d-flex flex-column align-items-center", children: _jsxs("div", { style: {
                                            fontSize: "14px",
                                            marginBottom: "12px",
                                            whiteSpace: "nowrap",
                                            position: "relative",
                                        }, children: [title ? `Loading ${title}` : "Loading data...", _jsx("div", { style: {
                                                    position: "absolute",
                                                    top: "100%",
                                                    left: "0",
                                                    right: "0",
                                                    marginTop: "8px",
                                                }, children: _jsx(LoadingBar, { width: "100%", height: "4px", duration: 1 }) })] }) }) })] })), isAggregateCard && actualIsLoading && (_jsx("div", { style: {
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            pointerEvents: "none",
                        }, children: _jsx("div", { style: { textAlign: "center", color: "var(--ps-blue, #374B94)" }, children: _jsxs("div", { style: { position: "relative", display: "inline-block", width: "200px" }, children: [_jsx("div", { style: { fontSize: "12px", fontWeight: "500", marginBottom: "8px" }, children: "Loading additional data..." }), _jsx(LoadingBar, { width: "200px", height: "4px", duration: 1, visible: true })] }) }) })), _jsx("div", { style: {
                            opacity: cardDataVisible ? 1 : 0,
                            transition: "opacity 0.5s ease-in-out",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }, children: _jsx("div", { style: {
                                opacity: cardContentVisible ? 1 : 0,
                                transition: "opacity 0.3s ease-in-out",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                            }, children: isDataLoaded && hasNoAPIData && !isAggregateCard ? (_jsx("div", { style: {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    minHeight: "505px",
                                    textAlign: "center",
                                    color: "var(--text-secondary, #666)",
                                }, children: _jsxs("div", { children: [_jsx("div", { style: { fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }, children: title }), _jsx("div", { style: { fontSize: "14px" }, children: "No interactions found for the selected date range" })] }) })) : showInlineInteractionList ? (_jsxs("div", { style: {
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                    position: "relative",
                                }, children: [_jsxs("div", { style: {
                                            padding: "16px",
                                            borderBottom: "1px solid #eee",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            backgroundColor: "#f8f9fa",
                                            position: "sticky",
                                            top: 0,
                                            zIndex: 10,
                                        }, children: [_jsx("div", { children: _jsx("h3", { style: { margin: 0, fontSize: "16px", fontWeight: "bold", color: "#333" }, children: inlineInteractionData.title }) }), _jsxs("div", { style: { display: "flex", alignItems: "center", gap: "12px" }, children: [_jsx(ExportButton, { title: inlineInteractionData.title.replace(/[^a-zA-Z0-9]/g, "_"), onExcelExport: handleExcelExport, exportElementId: "interaction-list-content", variant: "links", buttonStyle: { fontSize: "12px" }, enableFieldCustomization: true, availableFields: availableExportFields, onFieldCustomizationOpen: () => setShowFieldCustomizer(true), hidePdfExport: true }), _jsx("button", { onClick: handleCloseInlineInteractionList, style: {
                                                            background: "none",
                                                            border: "1px solid #ddd",
                                                            padding: "6px 12px",
                                                            borderRadius: "4px",
                                                            cursor: "pointer",
                                                            fontSize: "12px",
                                                            color: "var(--text-secondary, #666)",
                                                        }, onMouseEnter: (e) => {
                                                            e.currentTarget.style.backgroundColor = "#f0f0f0";
                                                        }, onMouseLeave: (e) => {
                                                            e.currentTarget.style.backgroundColor = "transparent";
                                                        }, children: "Back to Summary" })] })] }), _jsx("div", { style: {
                                            flex: 1,
                                            overflow: "auto",
                                            height: 0,
                                            minHeight: 0,
                                        }, children: _jsx(InteractionList, { interactions: inlineInteractionData.interactions, loading: false, onClose: handleCloseInlineInteractionList, title: "", className: "interactions-list-inline" }) })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "interactions-summary-card__header", style: {
                                            paddingBottom: "10px",
                                            borderBottom: "1px solid #eee",
                                        }, children: [_jsx("div", { style: {
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "10px",
                                                    marginLeft: "10px",
                                                }, children: _jsxs("div", { children: [_jsx("h3", { style: {
                                                                margin: 0,
                                                                fontSize: isAggregateCard ? "18px" : "16px",
                                                                fontWeight: "bold",
                                                                color: isAggregateCard ? "#374B94" : "#333",
                                                            }, children: title }), subtitle && (_jsx("p", { style: {
                                                                margin: "4px 0 0 0",
                                                                fontSize: "14px",
                                                                color: "var(--text-secondary, #666)",
                                                                fontWeight: "normal",
                                                            }, children: subtitle }))] }) }), _jsxs("div", { className: "d-flex align-items-center gap-2", children: [kpiResult && (_jsx("div", { style: {
                                                            marginRight: "16px",
                                                            opacity: kpiBannerVisible ? 1 : 0,
                                                            transform: kpiBannerVisible ? "translateY(0)" : "translateY(-10px)",
                                                            transition: "opacity 0.3s ease-in-out, transform 0.3s ease-in-out",
                                                            pointerEvents: kpiBannerVisible ? "auto" : "none",
                                                        }, children: _jsx(KPIDisplayBanner, { kpiResult: kpiResult, onPerformanceClick: handleKPIPerformanceClick, filteredInteractions: filteredInteractions, ...(onOpenKPIModal && { onEditKPI: onOpenKPIModal }) }) })), !selectedKPI && onOpenKPIModal && (_jsx(LinkButton, { onClick: () => {
                                                            if (onOpenKPIModal) {
                                                                onOpenKPIModal();
                                                            }
                                                        }, style: { marginRight: "16px" }, children: "Filter view by KPI" })), showViewSettingsButton && (_jsxs("button", { style: {
                                                            backgroundColor: "#374B94",
                                                            color: "white",
                                                            border: "1px solid #2a3a75",
                                                            padding: "6px 12px",
                                                            borderRadius: "4px",
                                                            fontSize: "11px",
                                                            fontWeight: "600",
                                                            cursor: "pointer",
                                                            whiteSpace: "nowrap",
                                                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "6px",
                                                        }, onClick: onViewSettingsClick, title: "View Settings", disabled: shouldShowLoading, onMouseEnter: (e) => {
                                                            e.currentTarget.style.backgroundColor = "#2a3a75";
                                                            e.currentTarget.style.transform = "translateY(-1px)";
                                                            e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
                                                        }, onMouseLeave: (e) => {
                                                            e.currentTarget.style.backgroundColor = "#374B94";
                                                            e.currentTarget.style.transform = "translateY(0)";
                                                            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.2)";
                                                        }, children: [_jsx(FontAwesomeIcon, { icon: faCog }), "View Settings"] }))] })] }), _jsxs("div", { className: "interactions-summary-card__content", children: [metricCards.length > 0 && (_jsx("div", { className: "interactions-metrics-row", children: metricCards.map((metric, index) => {
                                                    const isTotalInteractions = metric.label === "Total Interactions";
                                                    const isTransfers = metric.label === "Transfers";
                                                    const isClickable = isTotalInteractions || isTransfers;
                                                    return (_jsxs("div", { className: `interactions-metric-card ${isClickable ? "interactions-metric-card--clickable" : ""}`, onClick: isClickable ? () => handleMetricClick(metric.label) : undefined, style: {
                                                            cursor: isClickable ? "pointer" : "default",
                                                            transition: isClickable ? "background-color 0.2s, transform 0.1s" : "none",
                                                        }, onMouseEnter: (e) => {
                                                            if (isClickable) {
                                                                e.currentTarget.style.backgroundColor = "#f8f9fa";
                                                                e.currentTarget.style.transform = "translateY(-1px)";
                                                                const valueElement = e.currentTarget.querySelector(".interactions-metric-value");
                                                                if (valueElement) {
                                                                    valueElement.style.color = "#0056b3";
                                                                    valueElement.style.textDecoration = "underline";
                                                                }
                                                            }
                                                        }, onMouseLeave: (e) => {
                                                            if (isClickable) {
                                                                e.currentTarget.style.backgroundColor = "transparent";
                                                                e.currentTarget.style.transform = "translateY(0)";
                                                                const valueElement = e.currentTarget.querySelector(".interactions-metric-value");
                                                                if (valueElement) {
                                                                    valueElement.style.color = "#007bff";
                                                                    valueElement.style.textDecoration = "none";
                                                                }
                                                            }
                                                        }, children: [_jsx("div", { className: "interactions-metric-label", children: metric.label }), _jsx("div", { className: "interactions-metric-value", style: {
                                                                    color: isClickable ? "#007bff" : "inherit",
                                                                    textDecoration: "none",
                                                                    fontWeight: "bold",
                                                                }, children: typeof metric.value === "number" ? metric.value.toLocaleString() : metric.value })] }, index));
                                                }) })), _jsxs("div", { className: "interactions-summary-card__charts", children: [_jsxs("div", { className: "interactions-chart-container", children: [shouldShowLoading && (_jsx("div", { className: "interactions-chart-loading", children: _jsx("div", { className: "interactions-chart-loading-spinner" }) })), _jsx(DynamicBarChart, { data: volumeChartData, onBarDoubleClick: handleChartBarDoubleClick })] }), viewSettings.showPriorityAndOriginatorGraphs && (_jsx(DistributionGraphs, { interactions: interactions || [], summaryMetrics: summaryMetrics, priorityData: priorityData, originatorData: originatorData }))] }), _jsx("div", { style: {
                                                    position: "absolute",
                                                    bottom: "44px",
                                                    left: "16px",
                                                    right: "16px",
                                                    height: "1px",
                                                    backgroundColor: "#e0e0e0",
                                                } }), _jsx(ActionLinks, { title: title, interactions: interactions || [], locallyFilteredInteractions: locallyFilteredInteractions, kpiResult: kpiResult, selectedAccountNames: selectedAccountNames, selectedUserNames: selectedUserNames, viewSettings: viewSettings, summaryMetrics: summaryMetrics, isAggregateCard: isAggregateCard, hasData: hasData, onCallOutcomesClick: onCallOutcomesClick, onExecutiveSummaryClick: onExecutiveSummaryClick, onResponseMetricsClick: onResponseMetricsClick, onPresentationClick: onPresentationClick, onPdfExportClick: onPdfExportClick, setShowHelpDialog: setShowHelpDialog })] })] })) }) }), _jsx(HelpDialog, { isOpen: showHelpDialog, onClose: () => setShowHelpDialog(false) })] }), _jsx(PracticesFilter, { isAggregateCard: isAggregateCard, showPracticesFilter: viewSettings.showPracticesFilter, distinctObjectNames: distinctObjectNames, hasData: hasData, selectedObjectName: selectedObjectName, setSelectedObjectName: setSelectedObjectName, interactionsExcludingHangups: interactionsExcludingHangups }), _jsx(ScheduleFilter, { isAggregateCard: isAggregateCard, showScheduleNameFilter: viewSettings.showScheduleNameFilter, distinctScheduleNames: distinctScheduleNames, hasData: hasData, selectedScheduleName: selectedScheduleName, setSelectedScheduleName: setSelectedScheduleName, interactionsExcludingHangups: interactionsExcludingHangups }), _jsx(ExportFieldCustomizer, { isOpen: showFieldCustomizer, onClose: () => setShowFieldCustomizer(false), availableFields: availableExportFields, storageKey: "interaction-summary-export-fields", onSave: handleFieldCustomizationSave, sampleData: inlineInteractionData.interactions.slice(0, 10), fieldDefinitions: INTERACTION_EXPORT_FIELDS, configVersion: EXPORT_FIELDS_CONFIG_VERSION })] }));
};
export default InteractionsSummaryCard;
//# sourceMappingURL=InteractionsSummaryCard.js.map